import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Users, Plus, Edit2, Trash2, RotateCcw, X, Shield, ChevronDown,
  CheckCircle, Copy, Check, AlertTriangle, Loader, UserX,
  Key, Mail, Network, Crown
} from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const ROLES = ['manager', 'editor', 'analyst', 'viewer'];

const ROLE_META = {
  manager: { label: 'Manager', badge: 'sf-badge-manager', desc: 'Can manage campaigns, team members, and view analytics' },
  editor: { label: 'Editor', badge: 'sf-badge-success', desc: 'Can create and edit campaigns, view reports' },
  analyst: { label: 'Analyst', badge: 'sf-badge-warning', desc: 'Can view analytics and export reports' },
  viewer: { label: 'Viewer', badge: 'sf-badge-neutral', desc: 'Read-only access to analytics and reports' },
};

const STATUS_META = {
  active: { label: 'Active', badge: 'sf-badge-success' },
  pending: { label: 'Pending', badge: 'sf-badge-warning' },
  suspended: { label: 'Suspended', badge: 'sf-badge-error' },
  accepted: { label: 'Active', badge: 'sf-badge-success' },
  inactive: { label: 'Inactive', badge: 'sf-badge-neutral' },
};

function Badge({ children, className = '' }) {
  return <span className={cn('sf-badge', className)}>{children}</span>;
}

function Modal({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40">
      <div className="absolute inset-0 bg-transparent" onClick={onClose} />
      <div className="relative bg-white shadow-sm w-full max-w-md z-10 overflow-hidden animate-in zoom-in-95 duration-200 rounded-t-lg sm:rounded-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-[#D8DDE6] bg-[#F4F6F9] flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[12px] font-semibold text-[#706E6B] ">{label}</label>
      {children}
    </div>
  );
}

// Custom styled select to fix broken native dropdown styles
function StyledSelect({ value, onChange, options, disabled }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="w-full appearance-none bg-[#F3F2F2] border border-[#D8DDE6] rounded px-4 h-9 pr-10 text-[13px] text-[#080707] font-medium outline-none focus:bg-white focus:border-[#0176D3] transition-all cursor-pointer disabled:opacity-60 focus:ring-1 focus:ring-[#0176D3]"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] pointer-events-none" />
    </div>
  );
}

export default function TeamManagementPage() {
  const { token, user } = useSelector(state => state.auth);
  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Teams (hierarchy)
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', managerId: '', memberIds: [] });
  const [savingTeam, setSavingTeam] = useState(false);

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', email: '', role: 'editor' });
  const [inviting, setInviting] = useState(false);

  // Edit modal
  const [editMember, setEditMember] = useState(null);
  const [editRole, setEditRole] = useState('editor');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving] = useState(false);

  // Password reset modal
  const [resetMember, setResetMember] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [pwdCopied, setPwdCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, statsRes] = await Promise.all([
        api.get('/admin/team', { headers }),
        api.get('/admin/statistics', { headers }),
      ]);
      setMembers(membersRes.data.members || []);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Team fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'teams') fetchTeamsData();
  }, [tab]);

  const fetchTeamsData = async () => {
    setTeamsLoading(true);
    try {
      const [teamsRes, usersRes] = await Promise.all([
        api.get('/crm/teams', { headers }),
        api.get('/auth/team-members', { headers }),
      ]);
      setTeams(teamsRes.data.teams || []);
      setUsers(usersRes.data.teamMembers || usersRes.data.users || []);
    } catch (err) {
      console.error('Teams fetch error:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  const openCreateTeam = () => {
    setEditingTeam(null);
    setTeamForm({ name: '', description: '', managerId: '', memberIds: [] });
    setShowTeamModal(true);
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({
      name: team.name,
      description: team.description || '',
      managerId: team.managerId || '',
      memberIds: (team.members || []).map(m => m._id),
    });
    setShowTeamModal(true);
  };

  const handleSaveTeam = async () => {
    if (!teamForm.name.trim()) return;
    setSavingTeam(true);
    try {
      let teamId = editingTeam?._id;
      if (editingTeam) {
        await api.put(`/crm/teams/${editingTeam._id}`, {
          name: teamForm.name, description: teamForm.description, managerId: teamForm.managerId || null,
        }, { headers });
      } else {
        const res = await api.post('/crm/teams', {
          name: teamForm.name, description: teamForm.description, managerId: teamForm.managerId || null,
        }, { headers });
        teamId = res.data.team._id;
      }
      await api.put(`/crm/teams/${teamId}/members`, { userIds: teamForm.memberIds }, { headers });
      alertService.toast('success', editingTeam ? 'Team updated' : 'Team created');
      setShowTeamModal(false);
      fetchTeamsData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to save team');
    } finally {
      setSavingTeam(false);
    }
  };

  const handleDeleteTeam = async (team) => {
    const confirmed = await alertService.confirm(
      'Delete Team?',
      `This removes "${team.name}" and unassigns its members. Their records and permissions are unaffected.`,
      'warning',
      'Delete'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/crm/teams/${team._id}`, { headers });
      alertService.toast('success', 'Team deleted');
      fetchTeamsData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to delete team');
    }
  };

  const toggleTeamMember = (userId) => {
    setTeamForm(f => ({
      ...f,
      memberIds: f.memberIds.includes(userId) ? f.memberIds.filter(id => id !== userId) : [...f.memberIds, userId],
    }));
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return;
    setInviting(true);
    try {
      const res = await api.post('/admin/team/invite', inviteForm, { headers });
      if (res.data.emailSent === false) {
        alertService.error('Invitation Created', `Team member added, but the invitation email to ${inviteForm.email} could not be sent. Check SMTP settings in Platform Settings.`);
      } else {
        alertService.toast('success', `Invitation sent to ${inviteForm.email}`);
      }
      setShowInvite(false);
      setInviteForm({ firstName: '', lastName: '', email: '', role: 'editor' });
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async () => {
    setSaving(true);
    try {
      const trimmedEmail = editEmail.trim().toLowerCase();
      const emailChanged = trimmedEmail && trimmedEmail !== editMember.email;
      const roleChanged = editRole !== editMember.role;

      if (roleChanged) {
        await api.put(`/admin/team/${editMember._id}/role`, { role: editRole }, { headers });
      }
      if (emailChanged) {
        await api.put(`/admin/team/${editMember._id}/email`, { email: trimmedEmail }, { headers });
      }

      alertService.toast('success', 'Member updated successfully');
      setEditMember(null);
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member) => {
    const confirmed = await alertService.confirm(
      'Remove Team Member?',
      `This will permanently revoke ${member.email}'s access to your workspace.`,
      'warning',
      'Remove'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/admin/team/${member._id}`, { headers });
      alertService.toast('success', 'Member removed');
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleSuspend = async (member) => {
    try {
      await api.post(`/admin/team/${member._id}/suspend`, {}, { headers });
      alertService.toast('success', 'Member suspended');
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to suspend member');
    }
  };

  const handleReactivate = async (member) => {
    try {
      await api.post(`/admin/team/${member._id}/reactivate`, {}, { headers });
      alertService.toast('success', 'Member reactivated');
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to reactivate member');
    }
  };

  const handleResendInvitation = async (member) => {
    try {
      const res = await api.post(`/admin/team/${member._id}/resend-invitation`, {}, { headers });
      alertService.toast('success', res.data.emailSent ? 'Invitation resent' : 'Invitation renewed, but the email could not be sent — check SMTP settings');
      fetchData();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to resend invitation');
    }
  };

  const handleResetPassword = async () => {
    setResetting(true);
    try {
      const res = await api.post(`/admin/team/${resetMember._id}/reset-password`, {}, { headers });
      if (res.data.newPassword) {
        setGeneratedPassword(res.data.newPassword);
      } else {
        // Member never accepted their invite yet, so there's no account to
        // reset a password on — the backend resent the invite email instead.
        alertService.toast('success', res.data.emailSent ? 'Invitation resent' : 'Invitation renewed, but the email could not be sent — check SMTP settings');
        setResetMember(null);
      }
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to reset password');
      setResetMember(null);
    } finally {
      setResetting(false);
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setPwdCopied(true);
    setTimeout(() => setPwdCopied(false), 2000);
  };

  const openEdit = (member) => {
    setEditMember(member);
    setEditRole(member.role);
    setEditEmail(member.email);
  };

  const openReset = (member) => {
    setResetMember(member);
    setGeneratedPassword(null);
    setPwdCopied(false);
  };

  const memberStatus = (m) => {
    // `status` (active/inactive/suspended) reflects account-level access and
    // must win over invitationStatus — otherwise a suspended member who had
    // already accepted their invite shows as "Active" (invitationStatus is
    // still 'accepted', suspend never touches it).
    if (m.status === 'suspended') return 'suspended';
    if (m.status === 'inactive') return 'inactive';
    return m.invitationStatus || m.status || 'pending';
  };

  return (
    <div className={PAGE}>
      {/* Header */}
      <PageHeader
        title="Team & Permissions"
        icon={Users}
        subtitle="Manage your workspace members, roles, and access levels."
        actions={tab === 'members' ? (
          <button onClick={() => setShowInvite(true)} className="h-8 px-3 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] inline-flex items-center gap-2 whitespace-nowrap"><Plus size={14} /> Invite Member</button>
        ) : (
          <button onClick={openCreateTeam} className="h-8 px-3 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] inline-flex items-center gap-2 whitespace-nowrap"><Plus size={14} /> New Team</button>
        )}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#D8DDE6]">
        {[{ key: 'members', label: 'Members' }, { key: 'teams', label: 'Teams & Hierarchy' }].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
 'px-4 h-9 text-[13px] font-semibold border-b-2 transition-all -mb-px',
 tab === t.key ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#706E6B] hover:text-[#080707]'
 )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'teams' ? (
        <div className="space-y-6">
          {/* Distinct from Departments (crm/departments) — a Team here is a
              visibility/RBAC grouping (a manager sees their team's records),
              not a lead-routing target. For "route new leads to a default
              assignee," use Departments instead. */}
          <div className="sf-card px-4 py-3 flex items-center gap-3 bg-[#F4F6F9] border-[#D8DDE6]">
            <Network size={16} className="text-[#706E6B] shrink-0" />
            <p className="text-[12px] text-[#706E6B] font-medium">
              Teams control who can see whose records (a manager sees their team's data) — they don't route new leads.
              For lead-routing defaults, use{' '}
              <a href="/dashboard/crm/departments" className="text-[#0176D3] font-bold hover:underline">Departments</a>.
            </p>
          </div>
          {teamsLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loader size={28} className="animate-spin text-[#0176D3]" />
            </div>
          ) : teams.length === 0 ? (
            <div className="sf-card text-center py-16 space-y-3">
              <Network size={44} className="mx-auto text-[#D8DDE6]" />
              <p className="text-[#080707] font-bold text-[13px]">No teams yet</p>
              <p className="text-[#706E6B] text-[12px]">Group members under a team and assign a manager so they can see their reports' records.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teams.map(team => {
                const manager = users.find(u => u._id === team.managerId);
                return (
                  <div key={team._id} className="sf-card p-5 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-bold text-[#080707] text-[14px]">{team.name}</p>
                        {team.description && <p className="text-[12px] text-[#706E6B] mt-0.5">{team.description}</p>}
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => openEditTeam(team)}
                          title="Edit Team"
                          className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team)}
                          title="Delete Team"
                          className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#FDE7E9] hover:border-[#BA0517] hover:text-[#BA0517] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[12px]">
                      <Crown size={13} className="text-[#FF9900]" />
                      <span className="text-[#706E6B]">Manager:</span>
                      <span className="font-bold text-[#080707]">
                        {manager ? `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.email : <span className="italic text-[#706E6B]">Unassigned</span>}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      <p className="text-[11px] font-bold text-[#706E6B] uppercase ">Members ({(team.members || []).length})</p>
                      {(team.members || []).length === 0 ? (
                        <p className="text-[12px] text-[#706E6B] italic">No members assigned</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {team.members.map(m => (
                            <span key={m._id} className="sf-badge sf-badge-neutral">
                              {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="sf-card p-6 border-l-4 border-[#0176D3]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">Active Members</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{stats.totalMembers || 0}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#FF9900]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">Pending Invites</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{members.filter(m => memberStatus(m) === 'pending').length}</h3>
        </div>
        <div className="sf-card p-6 border-l-4 border-[#706E6B]">
          <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">Total Seats Used</p>
          <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{members.length}</h3>
        </div>
      </div>

      {/* Roles Legend */}
      <div className="sf-card p-4 bg-[#F4F6F9]">
        <p className="text-[11px] font-bold text-[#0176D3] uppercase mb-3 flex items-center gap-1.5">
          <Shield size={12} /> Role Hierarchy (Admin cannot be assigned — only the account owner holds admin access)
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {ROLES.map(role => (
            <div key={role} className="bg-white rounded-lg border border-[#E5E5E5] p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <Badge className={ROLE_META[role].badge}>{ROLE_META[role].label}</Badge>
              <p className="text-[11px] text-[#706E6B] mt-1.5 leading-relaxed">{ROLE_META[role].desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Members Table */}
      <div className="sf-table-container">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader size={28} className="animate-spin text-[#0176D3]" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <Users size={44} className="mx-auto text-[#D8DDE6]" />
            <p className="text-[#080707] font-bold text-[13px]">No team members yet</p>
            <p className="text-[#706E6B] text-[12px]">Click "Invite Member" to add your first team member</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => {
                  const status = memberStatus(member);
                  const statusMeta = STATUS_META[status] || STATUS_META.inactive;
                  const roleMeta = ROLE_META[member.role] || { badge: 'sf-badge-neutral', label: member.role };
                  return (
                    <tr key={member._id} className="group">
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded bg-[#0176D3] text-white flex items-center justify-center text-[13px] font-bold shrink-0">
                            {(member.firstName?.[0] || member.email?.[0] || '?').toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-[#080707] text-[13px]">
                              {member.firstName || member.lastName
                                ? `${member.firstName || ''} ${member.lastName || ''}`.trim()
                                : <span className="text-[#706E6B] italic font-medium">No name</span>}
                            </p>
                            {member.jobTitle && <p className="text-[11px] text-[#706E6B]">{member.jobTitle}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="text-[13px] text-[#706E6B]">{member.email}</td>
                      <td><Badge className={roleMeta.badge}>{roleMeta.label || member.role}</Badge></td>
                      <td><Badge className={statusMeta.badge}>{statusMeta.label}</Badge></td>
                      <td className="text-[13px] text-[#706E6B]">
                        {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5 opacity-0 group-hover:opacity-100 transition">
                          <button
                            onClick={() => openEdit(member)}
                            title="Edit Member"
                            aria-label="Edit Member"
                            className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F3F2F2] hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                          >
                            <Edit2 size={13} />
                          </button>
                          <button
                            onClick={() => openReset(member)}
                            title="Reset Password"
                            aria-label="Reset Password"
                            className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F3F2F2] hover:border-[#FF9900] hover:text-[#FF9900] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                          >
                            <Key size={13} />
                          </button>
                          {status === 'suspended' ? (
                            <button
                              onClick={() => handleReactivate(member)}
                              title="Reactivate Member"
                              aria-label="Reactivate Member"
                              className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F3F2F2] hover:border-[#2E844A] hover:text-[#2E844A] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                            >
                              <RotateCcw size={13} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSuspend(member)}
                              title="Suspend Access"
                              aria-label="Suspend Access"
                              className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F3F2F2] hover:border-[#FF9900] hover:text-[#FF9900] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                            >
                              <UserX size={13} />
                            </button>
                          )}
                          {!member.userId && (status === 'pending' || status === 'expired') ? (
                            <button
                              onClick={() => handleResendInvitation(member)}
                              title="Resend Invitation"
                              aria-label="Resend Invitation"
                              className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F3F2F2] hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                            >
                              <Mail size={13} />
                            </button>
                          ) : null}
                          <button
                            onClick={() => handleRemove(member)}
                            title="Remove Member"
                            aria-label="Remove Member"
                            className="h-8 w-8 flex items-center justify-center bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#FDE7E9] hover:border-[#BA0517] hover:text-[#BA0517] text-[#706E6B] transition-all shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Invite Modal ── */}
      {showInvite && (
        <Modal
          title="Invite Team Member"
          onClose={() => { setShowInvite(false); setInviteForm({ email: '', role: 'editor' }); }}
          footer={
            <>
              <button
                onClick={() => setShowInvite(false)}
                className="h-10 px-4 rounded border border-[#D8DDE6] text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2]"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteForm.email}
                className="h-9 px-5 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {inviting ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />}
                Send Invitation
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="First Name">
              <input
                type="text"
                value={inviteForm.firstName}
                onChange={e => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                placeholder="Jane"
                className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
              />
            </FormField>
            <FormField label="Last Name">
              <input
                type="text"
                value={inviteForm.lastName}
                onChange={e => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                placeholder="Doe"
                className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
              />
            </FormField>
          </div>

          <FormField label="Email Address *">
            <input
              type="email"
              required
              value={inviteForm.email}
              onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="team@example.com"
              className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
          </FormField>

          <FormField label="Role *">
            <StyledSelect
              value={inviteForm.role}
              onChange={e => setInviteForm({ ...inviteForm, role: e.target.value })}
              options={ROLES.map(r => ({ value: r, label: ROLE_META[r].label }))}
            />
            {inviteForm.role && ROLE_META[inviteForm.role] && (
              <p className="text-[11px] text-[#706E6B] mt-1.5 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
                {ROLE_META[inviteForm.role].desc}
              </p>
            )}
          </FormField>

          <div className="flex items-start gap-2.5 bg-[#FFF4E5] border border-[#FFCD94] rounded px-4 py-3">
            <AlertTriangle size={14} className="text-[#B35F00] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#B35F00] font-medium">
              Admin role is reserved for the workspace owner only and cannot be assigned to team members.
            </p>
          </div>
        </Modal>
      )}

      {/* ── Edit Role Modal ── */}
      {editMember && (
        <Modal
          title={`Edit Member — ${editMember.email}`}
          onClose={() => setEditMember(null)}
          footer={
            <>
              <button
                onClick={() => setEditMember(null)}
                className="h-10 px-4 rounded border border-[#D8DDE6] text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2]"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={saving || !editEmail.trim()}
                className="h-9 px-5 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Save Changes
              </button>
            </>
          }
        >
          <div className="flex items-center gap-3 p-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
            <div className="w-10 h-10 rounded bg-[#0176D3] text-white flex items-center justify-center font-bold text-[13px] shrink-0">
              {(editMember.firstName?.[0] || editMember.email?.[0] || '?').toUpperCase()}
            </div>
            <div>
              <p className="font-bold text-[#080707] text-[13px]">
                {editMember.firstName ? `${editMember.firstName} ${editMember.lastName || ''}`.trim() : editMember.email}
              </p>
              <p className="text-[11px] text-[#706E6B]">{editMember.email}</p>
            </div>
          </div>

          <FormField label="Email Address">
            <input
              type="email"
              value={editEmail}
              onChange={e => setEditEmail(e.target.value)}
              placeholder="team@example.com"
              className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
            <p className="text-[11px] text-[#706E6B] mt-1.5 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
              If this member has already accepted their invite, their login email updates too.
            </p>
          </FormField>

          <FormField label="Assign New Role">
            <StyledSelect
              value={editRole}
              onChange={e => setEditRole(e.target.value)}
              options={ROLES.map(r => ({ value: r, label: ROLE_META[r].label }))}
            />
            {editRole && ROLE_META[editRole] && (
              <p className="text-[11px] text-[#706E6B] mt-1.5 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
                {ROLE_META[editRole].desc}
              </p>
            )}
          </FormField>

          <div className="flex items-start gap-2.5 bg-[#FFF4E5] border border-[#FFCD94] rounded px-4 py-3">
            <AlertTriangle size={14} className="text-[#B35F00] mt-0.5 shrink-0" />
            <p className="text-[11px] text-[#B35F00] font-medium">
              Admin role cannot be assigned. Only the account owner holds admin-level access.
            </p>
          </div>
        </Modal>
      )}

      {/* ── Reset Password Modal ── */}
      {resetMember && (
        <Modal
          title={`Reset Password — ${resetMember.email}`}
          onClose={() => { setResetMember(null); setGeneratedPassword(null); }}
          footer={
            generatedPassword ? (
              <button
                onClick={() => { setResetMember(null); setGeneratedPassword(null); }}
                className="h-10 px-5 rounded bg-[#080707] hover:bg-[#292828] text-white text-[13px] font-semibold transition-all"
              >
                Done
              </button>
            ) : (
              <>
                <button
                  onClick={() => setResetMember(null)}
                  className="h-10 px-4 rounded border border-[#D8DDE6] text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={resetting}
                  className="h-9 px-5 rounded whitespace-nowrap bg-[#FF9900] hover:bg-[#B35F00] text-white text-[13px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {resetting ? <Loader size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  Generate New Password
                </button>
              </>
            )
          }
        >
          {!generatedPassword ? (
            <>
              <div className="flex items-center gap-3 p-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
                <div className="w-10 h-10 rounded bg-[#0176D3] text-white flex items-center justify-center font-bold text-[13px] shrink-0">
                  {(resetMember.firstName?.[0] || resetMember.email?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-[#080707] text-[13px]">
                    {resetMember.firstName ? `${resetMember.firstName} ${resetMember.lastName || ''}`.trim() : resetMember.email}
                  </p>
                  <p className="text-[11px] text-[#706E6B]">{resetMember.email}</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5 bg-[#FFF4E5] border border-[#FFCD94] rounded px-4 py-3">
                <AlertTriangle size={14} className="text-[#B35F00] mt-0.5 shrink-0" />
                <p className="text-[11px] text-[#B35F00] font-medium">
                  This will generate a new random password for this member. They will be required to change it on next login. Share the password securely.
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-[#2E844A]">
                <CheckCircle size={18} />
                <p className="text-[13px] font-bold">Password reset successfully!</p>
              </div>

              <div className="bg-[#080707] rounded px-5 py-4 flex items-center justify-between gap-4">
                <code className="text-[#4BC076] text-base font-mono select-all">{generatedPassword}</code>
                <button
                  onClick={copyPassword}
                  className={cn(
 'p-2 rounded transition-all shrink-0',
 pwdCopied ? 'bg-[#2E844A] text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
 )}
                  title="Copy password"
                  aria-label="Copy password"
                >
                  {pwdCopied ? <Check size={15} /> : <Copy size={15} />}
                </button>
              </div>

              <div className="flex items-start gap-2.5 bg-[#EAF4FE] border border-[#B8DAF3] rounded px-4 py-3">
                <Key size={14} className="text-[#0176D3] mt-0.5 shrink-0" />
                <p className="text-[11px] text-[#0176D3] font-medium">
                  Share this password with <strong>{resetMember.email}</strong> over a secure channel. The member will be prompted to change it upon their next login.
                </p>
              </div>
            </div>
          )}
        </Modal>
      )}
      </>
      )}

      {/* ── Team Modal ── */}
      {showTeamModal && (
        <Modal
          title={editingTeam ? `Edit Team — ${editingTeam.name}` : 'New Team'}
          onClose={() => setShowTeamModal(false)}
          footer={
            <>
              <button
                onClick={() => setShowTeamModal(false)}
                className="h-10 px-4 rounded border border-[#D8DDE6] text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2]"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTeam}
                disabled={savingTeam || !teamForm.name.trim()}
                className="h-9 px-5 rounded whitespace-nowrap bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {savingTeam ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                {editingTeam ? 'Save Changes' : 'Create Team'}
              </button>
            </>
          }
        >
          <FormField label="Team Name *">
            <input
              type="text"
              value={teamForm.name}
              onChange={e => setTeamForm({ ...teamForm, name: e.target.value })}
              placeholder="Sales — North"
              className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
          </FormField>

          <FormField label="Description">
            <input
              type="text"
              value={teamForm.description}
              onChange={e => setTeamForm({ ...teamForm, description: e.target.value })}
              placeholder="Optional"
              className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all"
            />
          </FormField>

          <FormField label="Manager">
            <StyledSelect
              value={teamForm.managerId}
              onChange={e => setTeamForm({ ...teamForm, managerId: e.target.value })}
              options={[{ value: '', label: 'Unassigned' }, ...users.map(u => ({
                value: u._id,
                label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
              }))]}
            />
            <p className="text-[11px] text-[#706E6B] mt-1.5 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
              The manager can view this team's members' records even when marked private.
            </p>
          </FormField>

          <FormField label="Members">
            <div className="max-h-48 overflow-y-auto border border-[#D8DDE6] rounded divide-y divide-[#D8DDE6]">
              {users.length === 0 ? (
                <p className="text-[12px] text-[#706E6B] italic p-3">No users available</p>
              ) : users.map(u => (
                <label key={u._id} className="flex items-center gap-2.5 px-3 py-2 text-[13px] cursor-pointer hover:bg-[#F4F6F9]">
                  <input
                    type="checkbox"
                    checked={teamForm.memberIds.includes(u._id)}
                    onChange={() => toggleTeamMember(u._id)}
                    className="accent-[#0176D3]"
                  />
                  <span className="text-[#080707]">{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</span>
                  <span className="text-[#706E6B] text-[11px] ml-auto">{u.email}</span>
                </label>
              ))}
            </div>
          </FormField>
        </Modal>
      )}
    </div>
  );
}
